"use strict";
/**
 * Instant UI feedback after row mutations: patch every local copy of a row ID
 * and recompute Age / Duration / LocalTime without waiting for full recent fetch.
 *
 * Timezone note: DB/Python store naive UTC. PHP recent treats those as UTC then
 * converts to the person timezone for LocalTime. JS Date.parse() treats naive
 * ISO datetimes as *local* — which is +10h wrong in Melbourne. Always parse UTC.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.patchFromSetTimeResponse = exports.syncOpenTimerMeta = exports.LocalRowPatchMemory = exports.patchRowsByIdInLists = exports.applyPatchToRow = exports.recomputeDerivedTimeFields = exports.formatLocalHhMm = exports.formatIntervalMs = exports.toEditDateValue = exports.toUtcIsoString = exports.parseUtcMs = void 0;
/**
 * Parse a pilot-notes timestamp as UTC milliseconds.
 * Accepts: Date, epoch ms, ISO with Z/+offset, MySQL "YYYY-MM-DD HH:mm:ss",
 * and naive ISO "YYYY-MM-DDTHH:mm:ss" (treated as UTC — not browser local).
 */
function parseUtcMs(value) {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (value instanceof Date) {
        var ms_1 = value.getTime();
        return Number.isNaN(ms_1) ? null : ms_1;
    }
    var text = String(value).trim();
    if (!text) {
        return null;
    }
    // MySQL / PHP style space separator → ISO
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)) {
        text = text.replace(' ', 'T');
    }
    // Already has explicit zone (Z or ±hh:mm after the time)
    var hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
    // Naive date-time (no zone) → force UTC. Critical for Melbourne etc.
    if (!hasZone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) {
        text = text + 'Z';
    }
    var ms = Date.parse(text);
    return Number.isNaN(ms) ? null : ms;
}
exports.parseUtcMs = parseUtcMs;
/** Normalize a UTC timestamp for storage on the row (always ends with Z). */
function toUtcIsoString(value) {
    if (value == null || value === '') {
        return value === '' ? '' : null;
    }
    var ms = parseUtcMs(value);
    if (ms == null) {
        return String(value);
    }
    // Prefer second precision to match server / datetime-local round-trips
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}
exports.toUtcIsoString = toUtcIsoString;
/**
 * Value for AngularJS `input[type=datetime-local]` ng-model.
 * Must be a Date (or null/empty) — ISO strings trigger ngModel:datefmt.
 */
function toEditDateValue(value) {
    if (value == null || value === '') {
        return value === '' ? '' : null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    var ms = parseUtcMs(value);
    if (ms == null) {
        return null;
    }
    return new Date(ms);
}
exports.toEditDateValue = toEditDateValue;
/** Rough PHP Time::formatInterval match for Age/Duration labels. */
function formatIntervalMs(ms) {
    if (!Number.isFinite(ms) || ms < 0) {
        ms = 0;
    }
    var totalSec = Math.floor(ms / 1000);
    var days = Math.floor(totalSec / 86400);
    var hours = Math.floor((totalSec % 86400) / 3600);
    var minutes = Math.floor((totalSec % 3600) / 60);
    var seconds = totalSec % 60;
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    if (days === 1) {
        return "1 day ".concat(hours, ":").concat(pad(minutes));
    }
    if (days > 1) {
        return "".concat(days, " days ").concat(hours, ":").concat(pad(minutes));
    }
    if (hours > 0) {
        return "".concat(hours, ":").concat(pad(minutes), ":").concat(pad(seconds));
    }
    return "".concat(minutes, ":").concat(pad(seconds));
}
exports.formatIntervalMs = formatIntervalMs;
/**
 * Local wall-clock HH:mm for display (browser TZ).
 * Matches PHP person-timezone LocalTime when the browser TZ matches the account.
 */
function formatLocalHhMm(utcMs) {
    var d = new Date(utcMs);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    var h = d.getHours();
    var m = d.getMinutes();
    return "".concat(h < 10 ? '0' : '').concat(h, ":").concat(m < 10 ? '0' : '').concat(m);
}
exports.formatLocalHhMm = formatLocalHhMm;
/**
 * Recompute server-style Age / Duration / LocalTime from pnCustomTimeUTC (+ optional end).
 * Open rows: Duration = age (start → now). Completed: Duration = start → completed.
 */
function recomputeDerivedTimeFields(row, nowMs) {
    if (nowMs === void 0) { nowMs = Date.now(); }
    var startMs = parseUtcMs(row.pnCustomTimeUTC);
    if (startMs == null) {
        return;
    }
    var endMs = nowMs;
    if (row.pnCompletedUTC != null && row.pnCompletedUTC !== '') {
        var parsed = parseUtcMs(row.pnCompletedUTC);
        if (parsed != null) {
            endMs = parsed;
        }
    }
    row.Age = formatIntervalMs(Math.max(0, nowMs - startMs));
    row.Duration = formatIntervalMs(Math.max(0, endMs - startMs));
    row.LocalTime = formatLocalHhMm(startMs);
}
exports.recomputeDerivedTimeFields = recomputeDerivedTimeFields;
var UTC_TIME_FIELDS = new Set(['pnCustomTimeUTC', 'pnCompletedUTC']);
function applyPatchToRow(row, patch) {
    if (!row || !patch) {
        return;
    }
    for (var _i = 0, _a = Object.keys(patch); _i < _a.length; _i++) {
        var key = _a[_i];
        if (patch[key] === undefined) {
            continue;
        }
        // Store naive-UTC API values as ISO-Z so later parses/filters stay correct
        if (UTC_TIME_FIELDS.has(key) && patch[key] != null && patch[key] !== '') {
            row[key] = toUtcIsoString(patch[key]);
        }
        else {
            row[key] = patch[key];
        }
    }
    // Keep edit form fields in sync when open.
    // datetime-local ng-models need Date objects (not ISO-Z strings).
    var edit = row.edit;
    if (edit && typeof edit === 'object') {
        if (patch.pnCustomTimeUTC !== undefined) {
            edit.pnCustomTimeUTC = toEditDateValue(patch.pnCustomTimeUTC);
        }
        if (patch.pnCompletedUTC !== undefined) {
            edit.pnCompletedUTC = toEditDateValue(patch.pnCompletedUTC);
        }
        if (patch.pnTopicDetail !== undefined) {
            edit.pnTopicDetail = patch.pnTopicDetail;
        }
        if (patch.pnInvoiceNote !== undefined) {
            edit.pnInvoiceNote = patch.pnInvoiceNote;
        }
        if (patch.pnAssetTag !== undefined) {
            edit.pnAssetTag = patch.pnAssetTag;
        }
        if (patch.pnFlowKind !== undefined) {
            edit.pnFlowKind = patch.pnFlowKind;
        }
    }
    recomputeDerivedTimeFields(row);
}
exports.applyPatchToRow = applyPatchToRow;
function patchRowsByIdInLists(lists, rowId, patch) {
    var idNum = Number(rowId);
    if (!idNum || !patch) {
        return 0;
    }
    var count = 0;
    for (var _i = 0, lists_1 = lists; _i < lists_1.length; _i++) {
        var list = lists_1[_i];
        if (!Array.isArray(list)) {
            continue;
        }
        for (var _a = 0, list_1 = list; _a < list_1.length; _a++) {
            var row = list_1[_a];
            if (!row || typeof row !== 'object') {
                continue;
            }
            var r = row;
            if (Number(r.ID) === idNum) {
                applyPatchToRow(r, patch);
                count += 1;
            }
        }
    }
    return count;
}
exports.patchRowsByIdInLists = patchRowsByIdInLists;
var DEFAULT_REMEMBER_MS = 45000;
var LocalRowPatchMemory = /** @class */ (function () {
    function LocalRowPatchMemory(rememberMs) {
        if (rememberMs === void 0) { rememberMs = DEFAULT_REMEMBER_MS; }
        this.rememberMs = rememberMs;
        this.byId = new Map();
    }
    LocalRowPatchMemory.prototype.remember = function (rowId, patch) {
        var id = Number(rowId);
        if (!id || !patch || !Object.keys(patch).length) {
            return;
        }
        var prev = this.byId.get(id);
        this.byId.set(id, {
            patch: __assign(__assign({}, ((prev === null || prev === void 0 ? void 0 : prev.patch) || {})), patch),
            expiresAt: Date.now() + this.rememberMs,
        });
    };
    /** Drop expired entries; return still-valid map. */
    LocalRowPatchMemory.prototype.activeEntries = function (nowMs) {
        var _this = this;
        if (nowMs === void 0) { nowMs = Date.now(); }
        var out = [];
        this.byId.forEach(function (entry, rowId) {
            if (entry.expiresAt < nowMs) {
                _this.byId.delete(rowId);
                return;
            }
            out.push({ rowId: rowId, patch: entry.patch });
        });
        return out;
    };
    /**
     * Re-apply remembered patches onto current list snapshots (after recent bind).
     * Returns number of list rows touched.
     */
    LocalRowPatchMemory.prototype.reapplyToLists = function (lists, nowMs) {
        if (nowMs === void 0) { nowMs = Date.now(); }
        var count = 0;
        for (var _i = 0, _a = this.activeEntries(nowMs); _i < _a.length; _i++) {
            var _b = _a[_i], rowId = _b.rowId, patch = _b.patch;
            count += patchRowsByIdInLists(lists, rowId, patch);
        }
        return count;
    };
    LocalRowPatchMemory.prototype.clear = function (rowId) {
        if (rowId == null) {
            this.byId.clear();
            return;
        }
        this.byId.delete(Number(rowId));
    };
    return LocalRowPatchMemory;
}());
exports.LocalRowPatchMemory = LocalRowPatchMemory;
/**
 * Keep the live open/session timer displays coherent after a local time patch.
 * - Start moved on the open row → reset openForSec + tick baseline.
 * - Open row completed → mark idle.
 */
function syncOpenTimerMeta(meta, rowId, patch, nowMs) {
    if (nowMs === void 0) { nowMs = Date.now(); }
    if (!meta || meta.openId == null) {
        return { meta: meta, resetTickBase: false };
    }
    if (Number(meta.openId) !== Number(rowId)) {
        return { meta: meta, resetTickBase: false };
    }
    if (patch.pnCompletedUTC != null && patch.pnCompletedUTC !== '') {
        return {
            meta: { openId: null, idle: true },
            resetTickBase: true,
        };
    }
    if (patch.pnCustomTimeUTC != null && patch.pnCustomTimeUTC !== '') {
        var startMs = parseUtcMs(patch.pnCustomTimeUTC);
        if (startMs != null) {
            var next = __assign(__assign({}, meta), { openForSec: Math.max(0, Math.floor((nowMs - startMs) / 1000)), startedUTC: toUtcIsoString(patch.pnCustomTimeUTC) });
            delete next.idle;
            return { meta: next, resetTickBase: true };
        }
    }
    return { meta: meta, resetTickBase: false };
}
exports.syncOpenTimerMeta = syncOpenTimerMeta;
/** Build a patch from set-time API response (+ optional row snapshot). */
function patchFromSetTimeResponse(res) {
    var _a, _b, _c;
    var patch = {};
    if (!res) {
        return patch;
    }
    if (res.pnCustomTimeUTC !== undefined) {
        // Normalize so UI never stores naive UTC that JS would read as local
        patch.pnCustomTimeUTC =
            res.pnCustomTimeUTC == null || res.pnCustomTimeUTC === ''
                ? res.pnCustomTimeUTC
                : toUtcIsoString(res.pnCustomTimeUTC);
    }
    var completed = (_a = res.pnCompletedUTC) !== null && _a !== void 0 ? _a : res.CompletedUTC;
    if (completed !== undefined) {
        patch.pnCompletedUTC =
            completed == null || completed === '' ? completed : toUtcIsoString(completed);
    }
    // Snapshot may use model to_dict keys
    var row = res.row;
    if (row) {
        if (patch.pnCustomTimeUTC == null && (row.start_time != null || row.pnCustomTimeUTC != null)) {
            patch.pnCustomTimeUTC = ((_b = row.pnCustomTimeUTC) !== null && _b !== void 0 ? _b : row.start_time);
        }
        if (patch.pnCompletedUTC == null && (row.pnCompletedUTC != null || row.end_time != null)) {
            // end_time on model is pnCustomTime2UTC — prefer explicit completed
            if (row.pnCompletedUTC != null) {
                patch.pnCompletedUTC = row.pnCompletedUTC;
            }
        }
        if (row.topic_detail != null || row.pnTopicDetail != null) {
            patch.pnTopicDetail = ((_c = row.pnTopicDetail) !== null && _c !== void 0 ? _c : row.topic_detail);
        }
        if (row.pnCompletedPersonPK != null) {
            patch.pnCompletedPersonPK = row.pnCompletedPersonPK;
        }
        if (row.pnSchedule != null) {
            patch.pnSchedule = row.pnSchedule;
        }
    }
    if (res.mode === 'end_from_next' || (typeof res.mode === 'string' && String(res.mode).includes('end_from_next'))) {
        if (patch.pnTopicDetail == null) {
            // PHP flips In Progress → Completed; safe default for open rows
            patch.pnTopicDetail = 'Completed';
        }
        if (patch.pnSchedule == null) {
            patch.pnSchedule = false;
        }
    }
    return patch;
}
exports.patchFromSetTimeResponse = patchFromSetTimeResponse;
